import _ from "lodash";

export const TAXONOMY_PREF_DRIVE_METADATA = "driveMetadataVocabulary";
export const TAXONOMY_PREF_ACTIVITY = "activityVocabulary";
export const TAXONOMY_PREF_ALLOW_MANUAL_TAGS = "taxonomyAllowManualTags";
export const TAXONOMY_DOCUMENT_KIND = "ambifi.controlledVocabulary";

export function isTaxonomyDocumentContent(content) {
	try {
		const taxonomy = _.isString(content) ? JSON.parse(content) : content;
		return _.isPlainObject(taxonomy) && taxonomy.kind === TAXONOMY_DOCUMENT_KIND;
	} catch (err) {
		return false;
	}
}

export function getTaxonomyPreferenceValue(selectedOrg, preferenceName) {
	return _.get(selectedOrg, ["preferencesDefinition", preferenceName], null)
		|| _.get(selectedOrg, ["preferences", preferenceName], null);
}

export function normalizeTaxonomyBooleanPreference(value, defaultValue = false) {
	let resolved = value;
	if (_.isPlainObject(resolved) && Object.prototype.hasOwnProperty.call(resolved, "value")) {
		resolved = resolved.value;
	}

	if (typeof resolved === "boolean") {
		return resolved;
	}

	if (_.isString(resolved)) {
		const normalized = resolved.trim().toLowerCase();
		if (["true", "yes", "1", "on", "enabled"].includes(normalized)) {
			return true;
		}
		if (["false", "no", "0", "off", "disabled"].includes(normalized)) {
			return false;
		}
	}

	if (typeof resolved === "number") {
		return resolved !== 0;
	}

	return defaultValue;
}

export function areManualTaxonomyTagsEnabled(selectedOrg, defaultValue = true) {
	return normalizeTaxonomyBooleanPreference(
		getTaxonomyPreferenceValue(selectedOrg, TAXONOMY_PREF_ALLOW_MANUAL_TAGS),
		defaultValue
	);
}

export function resolveTaxonomyDriveResource(preferenceValue) {
	if (!preferenceValue) {
		return null;
	}

	if (_.isPlainObject(preferenceValue.value)) {
		return preferenceValue.value;
	}

	if (_.isPlainObject(preferenceValue.resource)) {
		return preferenceValue.resource;
	}

	if (_.isPlainObject(preferenceValue.file)) {
		return preferenceValue.file;
	}

	if (_.isPlainObject(preferenceValue) && (preferenceValue.guid || preferenceValue.path)) {
		return preferenceValue;
	}

	if (_.isString(preferenceValue) && preferenceValue.trim()) {
		return { path: preferenceValue.trim() };
	}

	return null;
}

export function getTaxonomyDriveResourceSignature(resource) {
	if (!resource) {
		return "";
	}

	return resource.guid || resource.path || JSON.stringify(resource);
}

export function cleanTaxonomySelections(selections = {}) {
	return _.pickBy(selections || {}, (values) => Array.isArray(values) && values.length > 0);
}

export function slugifyTaxonomyId(value = "", fallback = "item") {
	const normalized = `${value || ""}`
		.trim()
		.toLowerCase()
		.replace(/['"]/g, "")
		.replace(/[^a-z0-9]+/g, "-")
		.replace(/^-+|-+$/g, "");

	return normalized || fallback;
}

export function createTaxonomyTemplate() {
	return {
		schemaVersion: "1.0.0",
		kind: TAXONOMY_DOCUMENT_KIND,
		id: "organization-controlled-vocabulary-v1",
		name: "Organization Controlled Vocabulary",
		description: "",
		intendedScopes: [
			"drive-media-assets",
			"activities"
		],
		defaultSelection: {
			allowMultipleFacets: true,
			allowMultipleValuesPerCategory: true,
			allowCustomValues: false,
			customValueWorkflow: "suggestion"
		},
		facets: [
			{
				id: "category",
				label: "Category",
				required: false,
				selection: "multi",
				values: [
					{
						id: "sample-tag",
						label: "Sample Tag"
					}
				]
			}
		]
	};
}

export function normalizeTaxonomyDocument(content) {
	let taxonomy = content;

	if (_.isString(taxonomy)) {
		const trimmed = taxonomy.trim();
		taxonomy = trimmed ? JSON.parse(trimmed) : createTaxonomyTemplate();
	}

	if (!_.isPlainObject(taxonomy)) {
		taxonomy = createTaxonomyTemplate();
	}

	return {
		...taxonomy,
		schemaVersion: taxonomy.schemaVersion || "1.0.0",
		kind: taxonomy.kind || TAXONOMY_DOCUMENT_KIND,
		facets: Array.isArray(taxonomy.facets) ? taxonomy.facets : []
	};
}

function normalizeValueEntry(value, fallbackId) {
	if (_.isString(value)) {
		return {
			id: slugifyTaxonomyId(value, fallbackId),
			label: value
		};
	}

	if (_.isPlainObject(value)) {
		const label = `${value.label || value.name || value.value || value.id || fallbackId || ""}`.trim();
		return {
			...value,
			id: value.id || slugifyTaxonomyId(label, fallbackId),
			label
		};
	}

	return {
		id: fallbackId,
		label: fallbackId
	};
}

export function getTaxonomySelectionKey(entry = {}) {
	return _.compact([entry.facetId, entry.categoryId]).join(".");
}

export function getTaxonomyValueKey(entry = {}) {
	return _.compact([entry.facetId, entry.categoryId, entry.valueId]).join(".");
}

function flattenValues(values = [], context = {}, result = [], parent = null) {
	(Array.isArray(values) ? values : []).forEach((rawValue, index) => {
		const fallbackId = `${context.categoryId || context.facetId || "value"}-${index + 1}`;
		const value = normalizeValueEntry(rawValue, fallbackId);
		const entry = {
			facetId: context.facetId,
			facetLabel: context.facetLabel,
			categoryId: context.categoryId,
			categoryLabel: context.categoryLabel,
			valueId: value.id,
			label: value.label,
			tag: value.label,
			aliases: Array.isArray(value.aliases) ? value.aliases : [],
			parentValueId: parent ? parent.id : null,
			parentValueLabel: parent ? parent.label : null
		};
		entry.selectionKey = getTaxonomySelectionKey(entry);
		entry.valueKey = getTaxonomyValueKey(entry);
		entry.pathLabels = _.compact([entry.facetLabel, entry.categoryLabel, entry.parentValueLabel, entry.label]);
		entry.pathIds = _.compact([entry.facetId, entry.categoryId, entry.parentValueId, entry.valueId]);
		result.push(entry);

		if (Array.isArray(value.children) && value.children.length > 0) {
			flattenValues(value.children, context, result, value);
		}
	});

	return result;
}

function createCategoryValueEntry(category, context = {}) {
	const categoryLabel = `${category.label || category.name || category.id || ""}`.trim();
	const categoryId = category.id || slugifyTaxonomyId(categoryLabel, "category");
	const entry = {
		entryType: "category",
		facetId: context.facetId,
		facetLabel: context.facetLabel,
		categoryId: "",
		categoryLabel: "",
		sourceCategoryId: categoryId,
		sourceCategoryLabel: categoryLabel,
		valueId: categoryId,
		label: category.tagLabel || categoryLabel,
		tag: category.tagLabel || categoryLabel,
		aliases: Array.isArray(category.aliases) ? category.aliases : [],
		parentValueId: null,
		parentValueLabel: null
	};
	entry.selectionKey = getTaxonomySelectionKey(entry);
	entry.valueKey = getTaxonomyValueKey(entry);
	entry.pathLabels = _.compact([entry.facetLabel, entry.label]);
	entry.pathIds = _.compact([entry.facetId, entry.valueId]);
	return entry;
}

export function flattenTaxonomyValues(taxonomyContent) {
	const taxonomy = normalizeTaxonomyDocument(taxonomyContent);
	const values = [];

	taxonomy.facets.forEach((rawFacet, facetIndex) => {
		const facet = _.isPlainObject(rawFacet) ? rawFacet : {};
		const facetLabel = `${facet.label || facet.name || facet.id || `Facet ${facetIndex + 1}`}`.trim();
		const facetId = facet.id || slugifyTaxonomyId(facetLabel, `facet-${facetIndex + 1}`);
		const categories = Array.isArray(facet.categories) ? facet.categories : [];

		if (categories.length > 0) {
			categories.forEach((rawCategory, categoryIndex) => {
				const category = _.isPlainObject(rawCategory) ? rawCategory : {};
				const categoryLabel = `${category.label || category.name || category.id || `Category ${categoryIndex + 1}`}`.trim();
				const categoryId = category.id || slugifyTaxonomyId(categoryLabel, `category-${categoryIndex + 1}`);
				if (category.selectable !== false) {
					values.push(createCategoryValueEntry({
						...category,
						id: categoryId,
						label: categoryLabel
					}, {
						facetId,
						facetLabel
					}));
				}
				flattenValues(category.values, {
					facetId,
					facetLabel,
					categoryId,
					categoryLabel
				}, values);
			});
		} else {
			flattenValues(facet.values, {
				facetId,
				facetLabel,
				categoryId: "",
				categoryLabel: ""
			}, values);
		}
	});

	return values;
}

export function addTaxonomySelection(selections = {}, entry = {}) {
	const selectionKey = getTaxonomySelectionKey(entry);
	const valueId = entry.valueId;
	if (!selectionKey || !valueId) {
		return selections || {};
	}

	return {
		...(selections || {}),
		[selectionKey]: _.uniq([...(selections[selectionKey] || []), valueId])
	};
}

export function removeTaxonomySelection(selections = {}, entry = {}) {
	const selectionKey = getTaxonomySelectionKey(entry);
	const valueId = entry.valueId;
	if (!selectionKey || !valueId || !selections[selectionKey]) {
		return selections || {};
	}

	const nextValues = selections[selectionKey].filter((item) => item !== valueId);
	const nextSelections = {
		...(selections || {})
	};
	if (nextValues.length > 0) {
		nextSelections[selectionKey] = nextValues;
	} else {
		delete nextSelections[selectionKey];
	}

	return nextSelections;
}

export function getSelectedTaxonomyValueKeys(selections = {}, taxonomyValues = []) {
	const selected = {};
	(taxonomyValues || []).forEach((entry) => {
		const selectionKey = getTaxonomySelectionKey(entry);
		if (selectionKey && Array.isArray(selections[selectionKey]) && selections[selectionKey].includes(entry.valueId)) {
			selected[entry.valueKey] = true;
		}
	});
	return selected;
}
